import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import type { LineCitation } from '../../services/ai/citations/citationTypes'
import { LINE_CITATION_ICON } from '../../services/ai/citations/citationTypes'
import { HoverBubble } from '../Common/Hover/HoverBubble'
import { StatusChip } from '../Common'
import { Button } from '../Common/Button'
import { useNls } from '../../nls/nlsProvider'

/**
 * Desktop floating excerpt card. Anchored to the chip's bounding rect
 * using Common HoverBubble variant="rich".
 */
export const CitationExcerptCard = ({
  id,
  citation,
  anchor,
  onClose,
  onOpenSource,
}: {
  id: string
  citation: LineCitation
  anchor: DOMRect
  onClose: () => void
  onOpenSource: () => void
}) => {
  const { t } = useNls()
  const Icon = LINE_CITATION_ICON[citation.sourceKind]

  const content = (
    <div
      id={id}
      // eslint-disable-next-line no-restricted-syntax -- citation excerpt card, rendered inline or as a hover card
      role="dialog"
      aria-labelledby={`${id}-title`}
      className="lineCiteCard"
    >
      <header className="lineCiteCard__header">
        <span className="lineCiteCard__glyph" aria-hidden="true">
          <Icon fontSize="inherit" />
        </span>
        <div className="lineCiteCard__titleGroup">
          <span className="lineCiteCard__kind">{citation.sourceKind.replace(/_/g, ' ')}</span>
          <strong id={`${id}-title`} className="lineCiteCard__title">{citation.title}</strong>
        </div>
      </header>
      <blockquote className="lineCiteCard__excerpt">{citation.excerpt}</blockquote>
      <footer className="lineCiteCard__footer">
        {citation.confidence === 'inferred' && (
          <span title={t('NLSAiCitationInferredTitle')}>
            <StatusChip size="sm" tone="warning">{t('NLSAiCitationInferred')}</StatusChip>
          </span>
        )}
        <Button
          size="sm"
          variant="tertiary"
          className="lineCiteCard__open"
          onClick={onOpenSource}
          trailingIcon={<ArrowForwardRoundedIcon fontSize="inherit" style={{ verticalAlign: '-2px' }} />}
        >
          {t('NLSAiCitationOpenSource')}
        </Button>
      </footer>
    </div>
  )

  return (
    <HoverBubble
      open
      variant="rich"
      interactive
      placement="auto"
      anchor={{ x: anchor.x, y: anchor.y, width: anchor.width, height: anchor.height }}
      onPointerLeave={onClose}
      onRequestClose={onClose}
      content={content}
    />
  )
}
